'use client';

import type {
	IGuestLogin,
	ILogin,
	IOAuthLogin,
	IOtpVerification,
	IPasswordChange,
	IPasswordForgot,
	IPasswordReset,
	ISignup,
	ISignupResendOtp,
	ISignupVerification,
} from '@/api';
import { authService } from '@/api';
import { ACCESS_TOKEN, REFRESH_TOKEN } from '@/constants/auth';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
export const useAuth = () => {
	const queryClient = useQueryClient();
	const router = useRouter();

	const loginMutation = useMutation({
		mutationFn: (data: ILogin) => authService.login(data),
		onSuccess: (response) => {
			const { accessToken, refreshToken } = response.data;
			localStorage.setItem(ACCESS_TOKEN, accessToken);
			localStorage.setItem(REFRESH_TOKEN, refreshToken);
			queryClient.invalidateQueries({ queryKey: ['user'] });
			router.push('/my_profile/centre');
		},
	});

	const oauthLoginMutation = useMutation({
		mutationFn: (data: IOAuthLogin) => authService.oauthLogin(data),
		onSuccess: (response) => {
			const { accessToken, refreshToken } = response.data;
			localStorage.setItem(ACCESS_TOKEN, accessToken);
			localStorage.setItem(REFRESH_TOKEN, refreshToken);
			queryClient.invalidateQueries({ queryKey: ['user'] });
			router.push('/my_profile/centre');
		},
	});

	const guestLoginMutation = useMutation({
		mutationFn: (data: IGuestLogin) => authService.guestLogin(data),
		onSuccess: (response) => {
			const { accessToken, refreshToken } = response.data;
			localStorage.setItem(ACCESS_TOKEN, accessToken);
			localStorage.setItem(REFRESH_TOKEN, refreshToken);
			queryClient.invalidateQueries({ queryKey: ['user'] });
			// router.push('/my_profile/centre');
		},
	});

	const signupMutation = useMutation({
		mutationFn: (data: ISignup) => authService.signup(data),
	});

	const changePasswordMutation = useMutation({
		mutationFn: (data: IPasswordChange) => authService.changePassword(data),
	});

	const resetPasswordMutation = useMutation({
		mutationFn: (data: IPasswordReset) => authService.resetPassword(data),
		onSuccess: () => {
			router.push('/my_profile/login');
		},
	});

	const forgotPasswordMutation = useMutation({
		mutationFn: (data: IPasswordForgot) => authService.forgotPassword(data),
	});

	const verifyOtpMutation = useMutation({
		mutationFn: (data: IOtpVerification) =>
			authService.verifyForgotPasswordOtp(data),
	});

	const verifySignupOtpMutation = useMutation({
		mutationFn: (data: ISignupVerification) => authService.verifySignup(data),
	});

	const resendSignupOtpMutation = useMutation({
		mutationFn: (data: ISignupResendOtp) => authService.resendSignupOtp(data),
	});

	return {
		login: loginMutation.mutate,
		oauthLogin: oauthLoginMutation.mutate,
		guestLogin: guestLoginMutation.mutate,
		signup: signupMutation.mutate,
		changePassword: changePasswordMutation.mutate,
		resetPassword: resetPasswordMutation.mutate,
		forgotPassword: forgotPasswordMutation.mutate,
		verifyOtp: verifyOtpMutation.mutate,
		verifySignupOtp: verifySignupOtpMutation.mutate,
		resendSignupOtp: resendSignupOtpMutation.mutate,
		logout: authService.logout,
		isLoading:
			loginMutation.isPending ||
			oauthLoginMutation.isPending ||
			guestLoginMutation.isPending ||
			signupMutation.isPending ||
			changePasswordMutation.isPending ||
			resetPasswordMutation.isPending ||
			forgotPasswordMutation.isPending ||
			verifyOtpMutation.isPending,
		error:
			loginMutation.error ||
			oauthLoginMutation.error ||
			guestLoginMutation.error ||
			signupMutation.error ||
			changePasswordMutation.error ||
			resetPasswordMutation.error ||
			forgotPasswordMutation.error ||
			verifyOtpMutation.error,
	};
};
